import { DraftState, StringInfo } from '../types';

/**
 * Whether a draft changes text customers already see — the classification that,
 * inside a change-control window, makes an MCM required rather than optional.
 *
 * Mirrors the backend gate (`ChangeControlGate.editsLiveStrings`; design §8.3,
 * https://chorus.aws.dev/doc/KxMqRG4cTs69): a deletion always counts; an
 * `edited` entry counts unless it is a pure variant addition — base text and note
 * identical to the live string, every live variant still present with the same
 * text and note, and the only change is variants for CX IDs the live string
 * lacks. New strings never count.
 *
 * The UI files a string under `edited` whenever anything about it changed,
 * including only its variants, so the list alone cannot answer this; each entry
 * is compared field by field against the live strings the page already holds.
 * This is a courtesy pre-check so the modal does not demand an MCM the backend
 * would not require; the backend remains the gate.
 *
 * Exact comparison, like the backend: an empty-vs-absent note counts as an edit,
 * erring towards asking for the MCM. An edited entry whose live string is not
 * loaded (should not happen after `validateDelta`-style checks, but the strings
 * list may still be loading) counts as a live edit for the same reason.
 */
export function draftEditsLiveStrings(
  draft: DraftState | null | undefined,
  liveStrings: readonly StringInfo[],
): boolean {
  if (!draft) {
    return false;
  }
  if (draft.deleted.length > 0) {
    return true;
  }
  const liveById = new Map(liveStrings.map((s) => [s.stringId, s]));
  return draft.edited.some((edited) => {
    const live = liveById.get(edited.stringId);
    return live === undefined || !isVariantAdditionOnly(edited, live);
  });
}

/**
 * The minimal shape {@link isVariantAdditionOnly} reads. Structural rather than
 * a union of the concrete types because the same comparison serves two
 * submission paths whose string types are generated independently: a draft
 * (`DraftStringInput` vs `StringInfo`) and a rollback (`StringInfoInput` vs
 * `VersionString`, see `rollbackEditsLiveStrings`). Only these three fields are
 * read, and all four types carry them.
 *
 * `variants` is optional and nullable because `StringInfoInput.variants` is
 * `InputMaybe<...>`; an absent list is treated as an empty one, which is what
 * "this string has no variants" means on every one of those types.
 */
export interface ComparableVariant {
  cxId: string;
  sourceText: string;
  contextNote?: string | null;
}

export interface ComparableString {
  sourceText: string;
  contextNote?: string | null;
  variants?: readonly ComparableVariant[] | null;
}

/**
 * Whether `submitted` differs from `live` ONLY by adding variants for CX IDs the
 * live string does not have — the `VariantAdditionsExempted` case the backend
 * gate lets through during a freeze.
 *
 * Exported so every submission path answers the gate's question with the same
 * code. Copying it per path is what the comment on
 * {@link draftEditsLiveStrings} warns against: a divergent copy either demands
 * an MCM the backend does not need, or — worse — fails to demand one it does.
 */
export function isVariantAdditionOnly(
  submitted: ComparableString,
  live: ComparableString,
): boolean {
  // Only text, note and variants are compared — the same three the backend's
  // `ChangeControlGate.isVariantAdditionOnly` reads from the TRANSCOM baseline.
  // `inlineMarkupType` / `messageFormatType` are deliberately not part of either
  // comparison: the gate guards text customers see, and the backend submit path
  // does not inspect them. Keep this list in step with the backend if it changes.
  if (
    submitted.sourceText !== live.sourceText ||
    normalizeNote(submitted.contextNote) !== normalizeNote(live.contextNote)
  ) {
    return false;
  }
  // Keyed by cxId. Two entries for one cxId mean there is no single submitted
  // value to compare against the live one, and last-wins could hide a rewrite
  // behind an unchanged duplicate — so, exactly as the backend does, treat it as
  // a live edit. A draft cannot produce this (`draftStorage.upsertVariant`
  // replaces by cxId), but a rollback snapshot can: `computeRollbackDelta`
  // rejects duplicate string IDs, not duplicate variant cxIds.
  const submittedVariants = submitted.variants ?? [];
  const submittedByCxId = new Map(submittedVariants.map((v) => [v.cxId, v]));
  if (submittedByCxId.size !== submittedVariants.length) {
    return false;
  }
  return (live.variants ?? []).every((existing) => {
    const kept = submittedByCxId.get(existing.cxId);
    return (
      kept !== undefined &&
      kept.sourceText === existing.sourceText &&
      normalizeNote(kept.contextNote) === normalizeNote(existing.contextNote)
    );
  });
}

/**
 * `null` and `undefined` both mean "no note"; anything else is compared as-is.
 * The live side is already normalized by `fetchVersionSnapshot` (`?? undefined`),
 * but a persisted draft is raw JSON and may carry `null`, so the comparison is
 * made tolerant on both sides to stay symmetric.
 */
function normalizeNote(note: string | null | undefined): string | undefined {
  return note ?? undefined;
}
